import { Component, OnInit, OnDestroy, ViewChild, ElementRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';

interface LogEntry {
  mac: string;
  level: number;
  message: string;
  timestamp: Date;
}
import {OutputCardComponent} from "../output-card/output-card.component";
import {InputCardComponent} from "../input-card/input-card.component";
import {ZigbeeCardComponent} from "../zigbee-card/zigbee-card.component";
import {NgFor, NgClass, NgIf, KeyValuePipe, DatePipe} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {ButtonCardComponent} from "../button-card/button-card.component";
import {debounceTime, Subject, Subscription} from "rxjs";
import {WebsocketService} from "../../services/websocket.service";
import {filter, pairwise, take} from "rxjs/operators";
import { AnsiColorPipe } from '../shared/ansi-color/ansi-color.pipe';
import { RouterModule } from '@angular/router';
import { ScenarioListComponent } from '../scenario-list/scenario-list.component';
import { IrrigationDashboardComponent } from '../irrigation-dashboard/irrigation-dashboard.component';
import { UniDashboardComponent } from './uni/uni-dashboard.component';
import { QRCodeModule } from 'angularx-qrcode';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-controller-details',
  templateUrl: './controller-details.component.html',
  styleUrls: ['./controller-details.component.scss'],
  standalone: true,
  imports: [TranslatePipe, OutputCardComponent,
    InputCardComponent,
    ZigbeeCardComponent,
    NgFor, 
    NgClass,
    NgIf,
    KeyValuePipe,
    FormsModule,
    ButtonCardComponent,
    AnsiColorPipe,
    RouterModule,
    DatePipe,
    ScenarioListComponent,
    IrrigationDashboardComponent,
    UniDashboardComponent,
    QRCodeModule,
    CommonModule
  ],
})
export class ControllerDetailsComponent implements OnInit, OnDestroy {
  private navigationSubscription: Subscription | null = null;
  private reauthSubscription: Subscription | null = null;
  controller: any;
  // Set once the device list has arrived and this mac is not among the user's controllers
  // (unknown mac, or one not shared with them): show "Controller not found" instead of an
  // empty card. Kept separate from `controller` so it doesn't flash while the list loads.
  notFound = false;
  activeTab: 'outputs' | 'inputs' | 'buttons' | 'zigbee' = 'outputs';
  previousTab: 'outputs' | 'inputs' | 'buttons' | 'zigbee' = 'outputs';

  // Zigbee sensors of this gateway, plus its rules — rules are part of the config, so they
  // reach the controller through the same "Upload to controller" as everything else.
  zigbeeDevices: any[] = [];
  zigbeeRules: any[] = [];
  pairing = false;
  pairingRemaining = 0;
  showInfoPopup: boolean = false;
  showServicePopup: boolean = false;
  showLogsPanel: boolean = false;
  logs: LogEntry[] = [];
  @ViewChild('logsContainer') logsContainer?: ElementRef;
  actionsMenuOpen = false;
  showRebootConfirm = false;
  showUpdateConfirm = false;
  private destroyRef = inject(DestroyRef);
  private timerTicker: any = null;
  showUploadConfirm = false;
  // Config sync state: an edit in the cloud does nothing until it is uploaded, and an edit
  // made on the controller itself is invisible here until it is imported. Show both.
  configState: { synced?: boolean; ahead?: string; cloudDate?: number; deviceDate?: number } | null = null;
  showImportConfirm = false;
  
  showDeleteConfirm = false;
  // A controller that sits on a bus group is nearly always operated together with its
  // neighbours, so the group view is the default; the group button collapses it to this
  // controller alone. Controllers without a groupID ignore this flag entirely.
  showGroupMembers = true;
  private toggleSubject = new Subject<any>();
  private websocketSubscription: Subscription | null = null;
  private updateIOSubscription: Subscription | null = null;
  private counterUpdateSubscription: Subscription | null = null;
  public formError: string = '';
  outputsGroups: { [key: string]: any[] } = {};
  inputsGroups: { [key: string]: any[] } = {};
  buttonsGroups: { [key: string]: any[] } = {};
  selectedScenario: any | null = null;
  // Latest SCENARIOEVENT for this controller, fed to the irrigation dashboard.
  scenarioRunning: any = null;

  // Which page layout to render, from the controller's saved settings. 'standard' is the
  // classic outputs/inputs/buttons view; 'irrigation' and 'light' are alternate themes.
  get theme(): 'standard' | 'irrigation' | 'light' {
    return this.controller?.settings?.theme || 'standard';
  }

  // Uni-family controller (water / heater / fan): channel-based, rendered by
  // app-uni-dashboard instead of the relay themes. Discriminated by the DTO's `type`.
  get isUni(): boolean {
    return this.controller?.type === 'uniController';
  }

  // The current user's access to this controller, from the device list. Drives what the UI
  // exposes: OWNER = everything incl. sharing; FULL = everything except owner-only actions;
  // OUTPUTS = only see/toggle outputs.
  get accessLevel(): string { return this.controller?.accessLevel || 'OWNER'; }
  get isOwner(): boolean { return this.accessLevel === 'OWNER'; }
  get canConfigure(): boolean { return this.accessLevel === 'OWNER' || this.accessLevel === 'FULL'; }
  get isOutputsOnly(): boolean { return this.accessLevel === 'OUTPUTS'; }

  // ---- sharing (owner only) ----
  showShareModal = false;
  shareLevel: 'FULL' | 'OUTPUTS' = 'OUTPUTS';
  shareLink = '';
  shareGenerating = false;
  shares: any[] = [];
  copied = false;

  openShare(): void {
    this.showShareModal = true;
    this.shareLink = '';
    this.loadShares();
  }
  loadShares(): void {
    this.websocketService.sendMessage({ type: 'SHARELIST', payload: { mac: this.controller?.mac } });
  }
  createShareLink(): void {
    this.shareGenerating = true;
    this.shareLink = '';
    this.websocketService.sendMessage({ type: 'SHARECREATE', payload: { mac: this.controller?.mac, level: this.shareLevel } });
  }
  copyLink(): void {
    if (!this.shareLink) return;
    const flag = () => { this.copied = true; setTimeout(() => (this.copied = false), 1500); };
    // navigator.clipboard exists only in a secure context (https/localhost); fall back to a
    // hidden textarea + execCommand so copy works over plain http too.
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(this.shareLink).then(flag).catch(() => this.fallbackCopy(flag));
    } else {
      this.fallbackCopy(flag);
    }
  }
  private fallbackCopy(done: () => void): void {
    const ta = document.createElement('textarea');
    ta.value = this.shareLink;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try { document.execCommand('copy'); done(); } catch { /* ignore */ }
    document.body.removeChild(ta);
  }
  revokeShare(userUuid: string): void {
    this.websocketService.sendMessage({ type: 'SHAREREVOKE', payload: { mac: this.controller?.mac, userUuid } });
  }
  changeShareLevel(userUuid: string, level: string): void {
    this.websocketService.sendMessage({ type: 'SHARESETLEVEL', payload: { mac: this.controller?.mac, userUuid, level } });
  }
  levelText(l: string): string { return l === 'FULL' ? 'share.level.full' : 'share.level.outputs'; }

  /**
   * Reset reason. The backend used to send a finished English sentence and throw the numeric
   * code away; it now sends the number, which we map to a key. A string still coming from an
   * older server is passed through untouched — translate leaves an unknown key as-is, so it
   * renders as the English text it already was.
   */
  get resetReasonText(): string {
    const r = this.controller?.info?.resetReason;
    if (r === null || r === undefined || r === '') return 'common.none';
    return typeof r === 'number' ? `controller.reset_reason.${r}` : String(r);
  }
    
  constructor(private route: ActivatedRoute,
              private router: Router,
              private websocketService: WebsocketService) {
    this.toggleSubject.pipe(debounceTime(300)).subscribe((command) => {
      if (this.controller?.mac) {
        this.websocketService.sendMessage({
          type: 'COMMAND',
          payload: {
            command: command,
            mac: this.controller.mac
          }
        });
      }
    });
  }
   
  setActiveTab(tab: 'outputs' | 'inputs' | 'buttons' | 'zigbee') {
    this.previousTab = this.activeTab;
    this.activeTab = tab;
  }

  getTabAnimationClass(tab: 'outputs' | 'inputs' | 'buttons' | 'zigbee'): string {
    if (tab === this.activeTab) {
      return 'active';
    }
    if (tab === this.previousTab) {
      const currentIndex = this.getTabIndex(tab);
      const newIndex = this.getTabIndex(this.activeTab);
      return newIndex > currentIndex ? 'slide-left' : 'slide-right';
    }
    return '';
  }

  private getTabIndex(tab: 'outputs' | 'inputs' | 'buttons' | 'zigbee'): number {
    switch(tab) {
      case 'outputs': return 0;
      case 'inputs': return 1;
      case 'buttons': return 2;
      case 'zigbee': return 3;
    }
  }

  compare( a: any, b: any ) {
    if ( a.id < b.id ){
      return -1;
    }
    if ( a.id > b.id ){
      return 1;
    }
    return 0;
  }

  findInputs(input: any[] | null | undefined): any[] {
    return input?.filter(p => p.id <= 15) || [];
  }

  findButtons(input: any[] | null | undefined): any[] {
    return input?.filter(p => p.id > 15) || [];
  }

  getNodesOutputsGroup(): { [key: string]: any[] } {
    if (!this.controller?.config.outputs) return {};
    const grouped: { [key: string]: any[] } = {};    
    return grouped;    
  }
  
  ngOnInit() {    
    const mac = this.route.snapshot.paramMap.get('mac');    
    // Restore active tab if navigation state provided (e.g., returning from edit)
    const navState: any = (window && (window.history && (window.history.state))) || {};
    if (navState && navState.activeTab) {
      this.activeTab = navState.activeTab;
      this.previousTab = navState.activeTab;
    }
    if (mac) {
      this.loadController(mac);
      this.setupWebSocketSubscription();
      this.navigationSubscription = this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.loadController(mac);
        }
      });
      // A STATUS sent while the socket was down is gone, and loadController's take(1)
      // subscription has completed — after a reconnect this card would stay stale until
      // navigation. Re-pull the snapshot on every authorization transition false -> true;
      // pairwise swallows the BehaviorSubject's replayed value so an already-authorized
      // socket doesn't duplicate the initial load.
      this.reauthSubscription = this.websocketService.authorized$
        .pipe(pairwise(), filter(([prev, curr]) => !prev && curr))
        .subscribe(() => this.loadController(mac));
    }
  }

  private loadController(mac: string) {
    //console.log('loadController ', mac);
    this.websocketService.getUserDevices()
      .pipe(
        // skip initial null emission so that a fresh USERDEVICES response can arrive
        filter((devices: any) => devices != null),
        take(1)
      )
      .subscribe((devices) => {
      if (devices != null) {
        this.controller = devices.find((ctrl: { mac: string; }) => ctrl.mac === mac);
    //console.log(this.controller);
        if (this.controller) {
          this.notFound = false;
          this.selectedScenario = null;
          this.seedScenarioRunning();
          this.buildAllGroups();
          this.configState = this.deriveConfigState(this.controller);
          this.loadZigbee();
          this.startTimerTicker();
        } else {
          this.notFound = true;
        }
      }
    });
  }

  // A controller reports scenario progress only when a run reaches a timed step, so opening
  // this page mid-scenario used to show "idle" until the next step — minutes away, or never.
  // The cloud remembers the last progress event and hands it over with the device list;
  // elapsedMs is measured server-side, so a browser clock that disagrees still counts down
  // correctly.
  private seedScenarioRunning(): void {
    const st = this.controller?.scenarioState;
    this.scenarioRunning = st ? { ...st, startedAt: Date.now() - (st.elapsedMs ?? 0) } : null;
  }

  private updateGroupStates() {
    if (this.outputsGroups) {
      const nodeOutputs = Object.values(this.outputsGroups).flat().filter(o => o.node === this.controller.mac);
      nodeOutputs.forEach((out: any) => {
        const state = (this.controller.ioStates.outputsStates & (1 << out.id)) !== 0;
        out.state = state ? "on" : "off";            
      });          
    }
    if (this.inputsGroups) {
      const nodeInputs = Object.values(this.inputsGroups).flat().filter(o => o.node === this.controller.mac);
      const counters = this.controller.ioStates?.countersStates || {};
      nodeInputs.forEach((inp: any) => {
        const state = (this.controller.ioStates.inputsStates & (1 << inp.id)) !== 0;
        inp.state = state ? "on" : "off";
        // The last reading the cloud stored: a counter must show its value straight away,
        // and keep showing it while the controller is offline.
        const stored = counters[String(inp.id)];
        if (inp.type === 'Counter' && stored !== undefined && stored !== null)
          inp.counterValue = stored;
      });
    }
  }

  private setupWebSocketSubscription() {
    // Unsubscribe from previous subscription if exists
    if (this.websocketSubscription) {
      this.websocketSubscription.unsubscribe();
    }

    if (this.updateIOSubscription) {
      this.updateIOSubscription.unsubscribe();
    }

    this.updateIOSubscription = this.websocketService.updateIO$.subscribe((update) => {
      //console.log('cd update', update);
      if (!this.controller || !update) {
        console.log('cd update null. return');
        return;
      }

      if (update.mac === this.controller.mac) {
        this.controller.ioStates.outputsStates = update.outputsStates;
        this.controller.ioStates.inputsStates = update.inputsStates;
      }
      
      if (this.outputsGroups) {
        // A node's state can reach the cloud twice: from the node itself (with its output
        // timers) and relayed by a neighbouring gateway over RS485 (which cannot know a
        // neighbour's timers). An update with no timers says nothing about them — it must
        // not overwrite them with zero, which is what made the countdown flicker away.
        const hasTimers = update.timers && Object.keys(update.timers).length > 0;
        const nodeOutputs = Object.values(this.outputsGroups).flat().filter(o => o.node === update.mac);
        nodeOutputs.forEach((out: any) => {
          const state = (update.outputsStates & (1 << out.id)) !== 0;
          out.state = state ? "on" : "off";
          if (hasTimers) out.timer = update.timers[out.id] ?? 0;
          else if (!state) out.timer = 0;      // switched off: whatever it was, it is over
        });
      }
      if (this.inputsGroups) {
        const nodeInputs = Object.values(this.inputsGroups).flat().filter(o => o.node === update.mac);
         //const nodeInputs = Object.values(this.inputsGroups).flat();
        //console.log(nodeInputs);
        nodeInputs.forEach((inp: any) => {
          const state = (update.inputsStates & (1 << inp.id)) !== 0;
          inp.state = state ? "on" : "off";          
        });        
      }
    })

    if (this.counterUpdateSubscription) {
      this.counterUpdateSubscription.unsubscribe();
    }
    this.counterUpdateSubscription = this.websocketService.counterUpdate$.subscribe((update) => {
      if (!update || !this.inputsGroups) return;
      const counters: Record<string, number> = update.counters || {};
      const nodeInputs = Object.values(this.inputsGroups).flat()
        .filter((inp: any) => inp.node === update.mac && inp.type === 'Counter');
      nodeInputs.forEach((inp: any) => {
        const val = counters[String(inp.id)];
        if (val !== undefined) inp.counterValue = val;
      });
    });

    // Scenario progress drives the irrigation dashboard's "watering now" view. Keep the
    // latest event for this controller; a new @Input reference lets ngOnChanges re-run.
    this.websocketService.scenarioEvent$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((ev: any) => {
      if (!ev?.mac || !this.controller || ev.mac !== this.controller.mac) return;
      // state != 0 means the run ended (finished, or cut short by a stop/all-off). Before
      // the device reported that, a manually stopped scenario kept its countdown on screen
      // for the rest of its nominal duration. Only the scenario being shown clears the card:
      // a device can run two at once, and the other one ending says nothing about this one.
      if (ev.state) {
        if (this.scenarioRunning?.scenarioId === ev.scenarioId) this.scenarioRunning = null;
      } else {
        this.scenarioRunning = { ...ev, startedAt: Date.now() };
      }
    });

    // Sharing: the generated invite link, and the current list of who has access.
    this.websocketService.on('SHAREINVITE').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((p: any) => {
      if (p?.mac !== this.controller?.mac) return;
      this.shareGenerating = false;
      this.shareLink = `${window.location.origin}/share/${p.token}`;
    });
    this.websocketService.on('SHARELIST').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((p: any) => {
      if (p?.mac !== this.controller?.mac) return;
      this.shares = p.shares || [];
    });

    // Subscribe to WebSocket messages to update controller info
    this.websocketService.on('ZIGBEEDEVICES').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((devices: any) => this.zigbeeDevices = devices || []);

    // A single sensor changed (joined, type detected, came back to life).
    this.websocketService.on('ZIGBEEDEVICE').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((dev: any) => {
      if (!dev?.ieee) return;
      const i = this.zigbeeDevices.findIndex(d => d.ieee === dev.ieee);
      if (i === -1) this.zigbeeDevices = [...this.zigbeeDevices, dev];
      else this.zigbeeDevices[i] = { ...this.zigbeeDevices[i], ...dev };
    });

    // Telemetry: keep the card's last reading fresh without refetching the list.
    this.websocketService.on('ZIGBEESENSOR').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((s: any) => {
      const d = this.zigbeeDevices.find(x => x.ieee === s?.ieee);
      if (!d) return;
      d.lastEvent = s.event;
      d.lastValue = s.value;
      if (s.battery) d.battery = s.battery;
      d.active = true;
    });

    this.websocketService.on('ZIGBEEPAIRING').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((p: any) => {
      this.pairing = !!p?.pairing;
      this.pairingRemaining = p?.remaining ?? 0;
    });

    this.websocketService.on('ZIGBEERULES').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((rules: any) => this.zigbeeRules = Array.isArray(rules) ? rules : []);

    this.websocketService.on('CONFIGSTATE').pipe(
      filter((st: any) => st?.mac === this.controller?.mac),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((st: any) => this.configState = st);

    this.websocketSubscription = this.websocketService.messages$.pipe(
      filter((message: any) =>
        (message.type === 'INFO' && message.payload?.mac === this.controller?.mac) ||
        (message.type === 'LOGS' && message.payload?.mac === this.controller?.mac) ||
        (message.type === 'SUCCESS') || (message.type === 'STATUS')
      )
    ).subscribe((message: any) => {
      if (message.type === 'INFO' && this.controller && message.payload) {
        this.controller.info = message.payload;
        this.updateGroupStates();
      } else if (message.type === 'LOGS' && message.payload) {
        const entry: LogEntry = {
          mac: message.payload.mac,
          level: message.payload.level ?? 0,
          message: message.payload.message ?? '',
          timestamp: new Date(),
        };
        this.logs = [...this.logs, entry];
        this.scrollLogsToBottom();
      } else if (message.type === 'SUCCESS') {
        if (this.showUploadConfirm)
          this.showUploadConfirm = false;        
      } else if (message.type === 'STATUS') {
        const mac = message?.payload?.mac;
        const online = message?.payload?.online;
        // The braces matter: without them the lastSeen update ran for EVERY controller's
        // STATUS, so any other controller going offline stamped this one's lastSeen.
        if (this.controller && mac === this.controller.mac) {
          this.controller.online = online;
          if (message.payload.directConnected !== undefined)
            this.controller.directConnected = message.payload.directConnected;
          if (!online) {
            this.controller.lastSeen = message.payload.lastSeen;
          }
        }
      }
    });
  }
  
  ngOnDestroy() {
    if (this.timerTicker) {
      clearInterval(this.timerTicker);
      this.timerTicker = null;
    }
    if (this.websocketSubscription) {
      this.websocketSubscription.unsubscribe();
    }
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
    if (this.reauthSubscription) {
      this.reauthSubscription.unsubscribe();
    }
    if (this.updateIOSubscription) {
      this.updateIOSubscription.unsubscribe();
    }
    if (this.counterUpdateSubscription) {
      this.counterUpdateSubscription.unsubscribe();
    }
  }

  // The banner must be right the moment the page opens, not only after the next event:
  // the controller record already carries both dates.
  private deriveConfigState(c: any) {
    const cloud = c?.configDate ? Math.floor(new Date(c.configDate).getTime() / 1000) : null;
    const device = c?.deviceConfigDate ? Math.floor(new Date(c.deviceConfigDate).getTime() / 1000) : null;
    return {
      cloudDate: cloud ?? undefined,
      deviceDate: device ?? undefined,
      synced: cloud !== null && cloud === device,
      ahead: cloud === null || device === null ? 'unknown'
           : cloud === device ? 'none'
           : cloud > device ? 'cloud' : 'device',
    };
  }

  // ---- Zigbee ----------------------------------------------------------------

  private startTimerTicker(): void {
    if (this.timerTicker) return;
    // The controller sends timers with an IO_STATE, i.e. only when something changes.
    // Counting down here keeps the card honest between those messages; the next IO_STATE
    // resyncs it.
    this.timerTicker = setInterval(() => {
      const outs = this.outputsGroups ? Object.values(this.outputsGroups).flat() : [];
      outs.forEach((out: any) => {
        if (out.state === 'on' && out.timer > 0) out.timer = out.timer - 1;
        else if (out.state !== 'on') out.timer = 0;
      });
    }, 1000);
  }

  /** Only the RCV2C has an 802.15.4 radio — the other models have no Zigbee at all. */
  get hasZigbee(): boolean {
    return this.controller?.model === 'RCV2C';
  }

  private loadZigbee(): void {
    const mac = this.controller?.mac;
    // Zigbee data is a configuration concern and its requests are FULL-gated on the server.
    // A shared outputs-only user must not fire them, or they'd get an "Access denied" toast.
    if (!mac || !this.hasZigbee || !this.canConfigure) return;
    this.websocketService.sendMessage({ type: 'ZIGBEEDEVICES', payload: { mac } });
    this.websocketService.sendMessage({ type: 'ZIGBEERULES', payload: { mac } });
  }

  rulesFor(ieee: string): number {
    return this.zigbeeRules.filter(r => r?.ieee === ieee).length;
  }

  startPairing(): void {
    this.websocketService.sendMessage({
      type: 'ZIGBEEDISCOVER',
      payload: { mac: this.controller?.mac, duration: 60 },
    });
  }

  stopPairing(): void {
    this.websocketService.sendMessage({
      type: 'ZIGBEESTOPDISCOVER',
      payload: { mac: this.controller?.mac },
    });
  }

  confirmImport() {
    this.showImportConfirm = false;
    this.toggleSubject.next('IMPORTCONFIG');
  }

  confirmUpload() {
    this.toggleSubject.next('UPLOADCONFIG');
    //this.toggleSubject.next('UPLOADALL');    
    this.cancelDialog();
  }

  confirmDelete() {
    this.toggleSubject.next('DELETE');
    this.cancelDialog();
  }

  showInfo(): void {
    this.showInfoPopup = true;
    this.toggleSubject.next('INFO');
  }

  showService() {
    this.showServicePopup = true;    
  }

  reboot() {
    this.toggleSubject.next('REBOOT');
  }

  showLogs() {
    this.showLogsPanel = true;
    this.enableSendLogs();
  }

  closeLogs() {
    this.showLogsPanel = false;
    this.disableSendLogs();
  }

  clearLogs() {
    this.logs = [];
  }

  enableSendLogs() {
    this.toggleSubject.next('ENABLESENDLOGS');
  }

  disableSendLogs() {
    this.toggleSubject.next('DISABLESENDLOGS');
  }

  getLogLevelClass(level: number): string {
    switch (level) {
      case 1: return 'log-error';
      case 2: return 'log-warning';
      case 3: return 'log-info';
      case 4: return 'log-debug';
      case 5: return 'log-verbose';
      default: return 'log-unknown';
    }
  }

  getLogLevelLabel(level: number): string {
    switch (level) {
      case 1: return 'ERR';
      case 2: return 'WRN';
      case 3: return 'INF';
      case 4: return 'DBG';
      case 5: return 'VRB';
      default: return 'UNK';
    }
  }

  private scrollLogsToBottom() {
    if (!this.showLogsPanel) return;
    setTimeout(() => {
      if (this.logsContainer?.nativeElement) {
        this.logsContainer.nativeElement.scrollTop = this.logsContainer.nativeElement.scrollHeight;
      }
    }, 0);
  }

  startOTA() {
    this.toggleSubject.next('STARTOTA');
  }

  toggleActionsMenu(event: Event) {
    event.stopPropagation();
    this.actionsMenuOpen = !this.actionsMenuOpen;
  }

  onMenuSettings() {
    this.actionsMenuOpen = false;
    this.router.navigate(['/controller', this.controller?.mac, 'settings']);    
  }

  onMenuInfo() {
    this.actionsMenuOpen = false;
    this.showInfo();
  }

  onMenuReboot() {
    this.actionsMenuOpen = false;
    this.showRebootConfirm = true;
  }

  onMenuUpdate() {
    this.actionsMenuOpen = false;
    this.showUpdateConfirm = true;
  }

  onMenuLogs() {
    this.actionsMenuOpen = false;
    this.showLogs();
  }

  confirmReboot() {
    this.showRebootConfirm = false;
    this.reboot();
  }

  confirmUpdate() {
    this.showUpdateConfirm = false;
    this.startOTA();
  }

  cancelDialog() {
    this.showRebootConfirm = false;
    this.showUpdateConfirm = false;
    this.showUploadConfirm = false;
    this.showDeleteConfirm = false;
  }

  onMenuDelete() {
    this.actionsMenuOpen = false;
    this.showDeleteConfirm = true;
  }

  onMenuGroup() {
    this.actionsMenuOpen = false;
    this.showGroupMembers = !this.showGroupMembers;
    // switching the group view - recompute everything with a single fetch
    this.buildAllGroups();
  }

  onMenuScenarios() {
    this.actionsMenuOpen = false;
    this.router.navigate(['/controller', this.controller?.mac, 'scenarios']);    
  }

  onMenuScheduler() {
    this.actionsMenuOpen = false;
    this.router.navigate(['/controller', this.controller?.mac, 'scheduler']);    
  }
    
  private buildAllGroups() {
    const currentGroup = this.controller?.groupID;
    // The Light theme always shows the whole bus group's outputs, even without the group
    // toggle — that is the point of the theme. Otherwise the group is expanded only on demand.
    // A shared outputs-only user gets the same toggle, but never the implicit Light-theme
    // expansion: their view is a plain outputs list that ignores the theme. getUserDevices()
    // returns only what is shared with them, so the group can never leak a foreign controller.
    const autoExpand = this.theme === 'light' && !this.isOutputsOnly;
    const wantGroup = !!currentGroup && (this.showGroupMembers || autoExpand);
    // только для одного устройства
    if (!wantGroup) {
      this.buildOutputsGroup();
      this.buildInputsGroup();
      this.buildButtonsGroup();
      this.updateGroupStates();
      return;
    }

    // для группы перестраиваем
    this.websocketService.getUserDevices().pipe().subscribe(devices => {
      const groupDevices = (devices || []).filter((d: any) => d?.groupID === currentGroup);
      const allOutputs = groupDevices.flatMap((d: any) => {
        // Seed each member's output states from its own ioStates; live UPDATEs keep them
        // current afterwards (updateGroupStates only refreshes this gateway's own outputs).
        const outStates = d?.ioStates?.outputsStates ?? 0;
        return (d?.config.outputs ?? [])
          .map((o: any) => ({ ...o, _deviceName: d.name, node: d.mac,
                              state: (outStates & (1 << o.id)) !== 0 ? 'on' : 'off' }));
      });

      const allInputs = groupDevices.flatMap((d: any) =>
        (d?.config.inputs ?? [])
          .filter((o: any) => o.id < 16)
          .map((o: any) => ({ ...o, _deviceName: d.name, node: d.mac, model: d.model }))
      );

      const allButtons = groupDevices.flatMap((d: any) =>
        (d?.config.inputs ?? [])
          .filter((o: any) => o.id > 15)
          .map((o: any) => ({ ...o, _deviceName: d.name, node: d.mac }))
      );

      this.outputsGroups = this.groupObjects(allOutputs);
      this.inputsGroups = this.groupObjects(allInputs);
      this.buttonsGroups = this.groupObjects(allButtons);
    });
  }
  
  private groupObjects(objs: any[] = []): Record<string, any[]> {
    return objs.reduce((map, o) => {
      (map[o._deviceName] ??= []).push(o);
      return map;
    }, {} as Record<string, any[]>);
  }

  private buildInputsGroup() {
    const currentGroup = this.controller?.groupID;
        
    if (!currentGroup || !this.showGroupMembers) {
      const inputs = (this.controller?.config.inputs ?? [])
        .filter((o: any) => o.id < 16)
        .map((o: any) => ({ ...o, _deviceName: this.controller.name, node: this.controller.mac, model: this.controller.model }));
      this.inputsGroups = this.groupObjects(inputs);      
    }
  }

  private buildOutputsGroup() {
    const currentGroup = this.controller?.groupID;
        
    if (!currentGroup || !this.showGroupMembers) {
      const outputs = (this.controller?.config.outputs ?? [])
        .map((o: any) => ({ ...o, _deviceName: this.controller.name, node: this.controller.mac }));
      this.outputsGroups = this.groupObjects(outputs);
      console.log('outputsGroups ', this.outputsGroups);      
    }
  }

  private buildButtonsGroup() {
    const currentGroup = this.controller?.groupID;
        
    if (!currentGroup || !this.showGroupMembers) {
      const buttons = (this.controller?.config.inputs ?? [])
        .filter((o: any) => o.id > 15)
        .map((o: any) => ({ ...o, _deviceName: this.controller.name, node: this.controller.mac }));
      this.buttonsGroups = this.groupObjects(buttons);      
    }
  }

  get showOutputGroupDividers(): boolean {
    return Object.keys(this.outputsGroups || {}).length > 1;
  }

  get showInputGroupDividers(): boolean {
    return Object.keys(this.inputsGroups || {}).length > 1;
  }

  get showButtonGroupDividers(): boolean {
    return Object.keys(this.buttonsGroups || {}).length > 1;
  }

  // trackBy identifies the ITEM, not its value. Putting the state or the timer in the key
  // made Angular think the output had been replaced by a different one on every tick: it
  // destroyed and rebuilt the card each second, which is what made the timer flicker.
  trackByOutput(index: number, item: any) {
    return item?.eid ?? item?.id ?? index;
  }

  trackByGroupKey(index: number, item: any) {
    return item?.key ?? index;
  }

  trackByInput(index: number, item: any) {
    return item?.eid ?? item?.id ?? index;
  }

  // Scenario management methods
  openScenario(scenario: any) {
    this.selectedScenario = scenario;
  }

  onScenariosBack() {
    this.selectedScenario = null;
  }

  onScenariosSave(scenarios: any[]) {
    if (this.controller) {
      this.controller.scenarios = scenarios;
      this.websocketService.sendMessage({
        type: 'COMMAND',
        payload: {
          command: 'save_scenarios',
          mac: this.controller.mac,
          scenarios: scenarios
        }
      });
    }
  }

  /**
   * The HH:MM:SS part only. Days are rendered separately by the template, because
   * "N days" needs a translated (and, in Russian, plural-sensitive) unit — see
   * uptimeDays() and the controller.info.uptime_days key.
   */
  getUptimeStr(uptime: number): string {
    const hours = Math.floor((uptime % 86400) / 3600);
    const minutes = Math.floor((uptime % 3600) / 60);
    const seconds = Math.floor(uptime % 60);

    return [hours, minutes, seconds]
      .map(v => v.toString().padStart(2, '0'))
      .join(':');
  }

  /** Whole days of uptime, or null when under a day so the template omits the prefix. */
  uptimeDays(uptime: number): number | null {
    const days = Math.floor(uptime / 86400);
    return days > 0 ? days : null;
  }
}
